import { zodResolver } from "@hookform/resolvers/zod";
import { memo, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { StyledInput } from "@/components/StyledInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { IDesktopBridge } from "@/lib/desktop";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

interface IChangeTenantDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  bridge: IDesktopBridge;
}

const buildSchema = (invalidMessage: string) =>
  z.object({
    url: z
      .string()
      .trim()
      .min(1, invalidMessage)
      .refine((value) => {
        try {
          const parsed = new URL(value);
          return parsed.protocol === "https:" || parsed.protocol === "http:";
        } catch {
          return false;
        }
      }, invalidMessage),
  });

type TFormValues = z.infer<ReturnType<typeof buildSchema>>;

const ChangeTenantDialogComponent = ({
  isOpen,
  onOpenChange,
  bridge,
}: IChangeTenantDialogProps) => {
  const { t } = useTranslation("auth");
  const [defaultTenantUrl, setDefaultTenantUrl] = useState<string>("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const invalidMessage = t("change_tenant.error_invalid");

  const form = useForm<TFormValues>({
    ...FORM_VALIDATION_OPTIONS,
    defaultValues: { url: "" },
    resolver: zodResolver(buildSchema(invalidMessage)),
  });

  useEffect(() => {
    let isMounted = true;
    if (isOpen) {
      Promise.all([bridge.getTenantUrl(), bridge.getDefaultTenantUrl()])
        .then(([current, fallback]) => {
          if (!isMounted) {
            return;
          }
          setDefaultTenantUrl(fallback);
          form.reset({ url: current });
          setSubmitError(null);
        })
        .catch((err) => {
          // NOTE: IPC failure / main-process crash — log + surface, but
          // keep the dialog usable so the user can retry or cancel.
          console.error("[ChangeTenantDialog] failed to load tenant info", err);
          if (!isMounted) {
            return;
          }
          setSubmitError(invalidMessage);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [bridge, form, invalidMessage, isOpen]);

  const handleSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const result = await bridge.setTenantUrl(values.url);
      if (!result.ok) {
        setSubmitError(result.error ?? invalidMessage);
        setIsSubmitting(false);
      }
      // NOTE: on success the main process tears the window down and reloads,
      // so we don't need to close the dialog ourselves.
    } catch (err) {
      console.error("[ChangeTenantDialog] setTenantUrl failed", err);
      setSubmitError(invalidMessage);
      setIsSubmitting(false);
    }
  });

  const handleReset = useCallback(async () => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const result = await bridge.resetTenantUrl();
      if (!result.ok) {
        setSubmitError(result.error ?? invalidMessage);
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("[ChangeTenantDialog] resetTenantUrl failed", err);
      setSubmitError(invalidMessage);
      setIsSubmitting(false);
    }
  }, [bridge, invalidMessage]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-tenant-dialog">
        <DialogHeader>
          <DialogTitle>{t("change_tenant.title")}</DialogTitle>
          <DialogDescription>
            {t("change_tenant.description")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              control={form.control}
              name="url"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormLabel>{t("change_tenant.label")}</FormLabel>
                  <FormControl>
                    <StyledInput
                      {...field}
                      autoFocus
                      placeholder={defaultTenantUrl}
                      isError={!!error}
                      data-testid="change-tenant_url-input"
                    />
                  </FormControl>
                  <FormMessage data-testid="change-tenant_url-error" />
                </FormItem>
              )}
            />
            {submitError && (
              <p
                className="text-sm text-destructive"
                data-testid="change-tenant_submit-error"
              >
                {submitError}
              </p>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={isSubmitting}
                data-testid="change-tenant_reset-button"
              >
                {t("change_tenant.reset")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                data-testid="change-tenant_cancel-button"
              >
                {t("change_tenant.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="change-tenant_save-button"
              >
                {t("change_tenant.save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export const ChangeTenantDialog = memo(ChangeTenantDialogComponent);
